<template>
  <div class="blend-property" style="--blend-type-columns: 4" data-testid="fillet-property-section">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <PuppyCollapseItem title="圆角类型" name="type">
        <div class="type-grid" data-testid="fillet-type-grid">
          <button
            v-for="option in filletTypeOptions"
            :key="option.value"
            type="button"
            class="type-card"
            :class="{ 'is-active': filletType === option.value }"
            :data-testid="option.testId"
            :title="option.title"
            @click="handleFilletTypeChange(option.value)"
          >
            <BlendTypeGlyph :icon-key="option.iconKey" />
          </button>
        </div>
      </PuppyCollapseItem>

      <PuppyCollapseItem title="要圆角化的项目" name="items">
        <div class="section-grid" data-testid="fillet-items-panel">
          <template v-if="isFaceSetMode">
            <PuppyFormItem
              v-for="group in faceSetGroups"
              :key="group.id"
            >
              <template #icon>
                <span
                  class="face-set-label"
                  :title="group.label"
                >
                  <span>{{ group.label }}</span>
                </span>
              </template>
              <PuppyInputSelect
                :items="group.items"
                :active="filletSelectionTarget === 'faces' && activeFaceSetId === group.id"
                :active-index="resolveActiveIndex(group.items, selectedFilletItemRef)"
                :selected-indices="resolveSelectedIndices(group.items, selectedFilletItemRef)"
                :visible-rows="3"
                item-key-field="persistentRef"
                item-label-field="label"
                :empty-text="`尚未选择${group.label}`"
                @update:active="(active) => handleFaceSetInputActiveChange(group.id, active)"
                @item-click="handleFaceSetItemClick(group.id, $event.item)"
                @delete-item="handleFaceSetItemRemove(group.id, $event.item)"
                @delete-all="handleFaceSetDeleteAll(group.id)"
              />
            </PuppyFormItem>

          </template>

          <template v-else>
            <PuppyFormItem>
              <template #icon>
                <span class="field-label-text">项目</span>
              </template>
              <PuppyInputSelect
                :items="selectedItems"
                :active="filletSelectionTarget === 'faces'"
                :active-index="resolveActiveIndex(selectedItems, selectedFilletItemRef)"
                :selected-indices="resolveSelectedIndices(selectedItems, selectedFilletItemRef)"
                :visible-rows="3"
                item-key-field="persistentRef"
                item-label-field="label"
                empty-text="尚未选择边线或面"
                @update:active="handlePrimaryInputActiveChange"
                @item-click="handlePrimaryItemClick($event.item)"
                @delete-item="handlePrimaryItemRemove($event.item)"
                @delete-all="handlePrimaryDeleteAll"
              />
            </PuppyFormItem>
          </template>

        </div>
      </PuppyCollapseItem>

      <PuppyCollapseItem
        :title="parametersPanelTitle"
        name="parameters"
      >
        <div class="section-grid" data-testid="fillet-parameters-panel">
          <template v-if="isConstantMode">
            <PuppyFormItem>
              <template #icon>
                <span class="field-label-text">模式</span>
              </template>
              <n-select
                size="small"
                :value="constantEdgeFilletMode"
                :options="edgeFilletModeOptions"
                :disabled="constantEdgeModeLocked"
                @update:value="handleEdgeFilletModeChange"
              />
            </PuppyFormItem>

            <PuppyFormItem>
              <template #icon>
                <span class="field-label-text">半径</span>
              </template>
              <div class="field-with-unit">
                <n-input-number
                  size="small"
                  show-button
                  :value="radiusValue"
                  :disabled="!context.setFilletRadius"
                  :min="FILLET_RADIUS_MIN"
                  :precision="3"
                  :step="1"
                  @update:value="handleRadiusCommit"
                />
                <span class="field-unit">mm</span>
              </div>
            </PuppyFormItem>

            <PuppyFormItem v-if="constantEdgeFilletMode === 'asymmetric'">
              <template #icon>
                <span class="field-label-text">半径 2</span>
              </template>
              <div class="field-with-unit">
                <n-input-number
                  size="small"
                  show-button
                  :value="radius2Value"
                  :disabled="!context.setFilletRadius2"
                  :min="FILLET_RADIUS_MIN"
                  :precision="3"
                  :step="1"
                  @update:value="handleRadius2Commit"
                />
                <span class="field-unit">mm</span>
              </div>
            </PuppyFormItem>

            <PuppyFormItem v-if="constantEdgeFilletMode === 'asymmetric' && isFieldVisible('asymmetricRadiiFlip')">
              <template #icon>
                <span class="field-label-text">翻转</span>
              </template>
              <n-checkbox
                :checked="asymmetricRadiiFlipped"
                :disabled="!isFieldEnabled('asymmetricRadiiFlip') || !context.setFilletAsymmetricRadiiFlipped"
                @update:checked="(value) => context.setFilletAsymmetricRadiiFlipped?.(value)"
              >
                翻转半径方向
              </n-checkbox>
            </PuppyFormItem>
          </template>

          <template v-if="isVariableMode">
            <PuppyFormItem>
              <template #icon>
                <span class="field-label-text">模式</span>
              </template>
              <n-select
                size="small"
                :value="variableMode"
                :options="variableModeOptions"
                :disabled="!context.setFilletVariableMode"
                @update:value="handleVariableModeChange"
              />
            </PuppyFormItem>

            <PuppyFormItem v-if="isFieldVisible('profile')">
              <template #icon>
                <span class="field-label-text">轮廓</span>
              </template>
              <n-select
                size="small"
                :value="profileValue"
                :options="profileOptions"
                disabled
              />
            </PuppyFormItem>

            <PuppyFormItem v-if="isFieldVisible('profile')">
              <template #icon>
                <span class="field-label-text">过渡</span>
              </template>
              <n-select
                size="small"
                :value="variableTransitionMode"
                :options="variableTransitionOptions"
                :disabled="!context.setFilletVariableTransition"
                @update:value="handleVariableTransitionChange"
              />
            </PuppyFormItem>

            <PuppyFormItem>
              <template #icon>
                <span class="field-label-text">实例数</span>
              </template>
              <n-input-number
                size="small"
                show-button
                :value="variableInstanceCount"
                :min="0"
                :max="24"
                :step="1"
                :precision="0"
                :disabled="!context.setFilletVariableInstanceCount"
                @update:value="handleVariableInstanceCountChange"
              />
            </PuppyFormItem>

            <PuppyFormItem>
              <template #icon>
                <span class="field-label-text">半径点</span>
              </template>
              <PuppyInputSelect
                :items="variablePointItems"
                :active="false"
                :active-index="resolveActiveIndex(variablePointItems, selectedVariablePointKey)"
                :selected-indices="resolveSelectedIndices(variablePointItems, selectedVariablePointKey)"
                :visible-rows="3"
                item-key-field="persistentRef"
                item-label-field="label"
                :is-item-delete-enabled="isVariablePointDeleteEnabled"
                delete-all-label="删除所有实例点"
                empty-text="未选择变量半径点"
                @item-click="handleVariablePointItemClick($event.item)"
                @delete-item="handleVariablePointItemRemove($event.item)"
                @delete-all="handleVariablePointDeleteAll"
              />
            </PuppyFormItem>

            <PuppyFormItem v-if="variableMode === 'symmetric'">
              <template #icon>
                <span class="field-label-text">半径</span>
              </template>
              <div class="field-with-unit">
                <n-input-number
                  size="small"
                  show-button
                  :value="formatNullableLengthForPropertyManager(selectedVariablePoint?.radius ?? null)"
                  :disabled="!selectedVariablePoint || !context.setFilletVariablePointRadius"
                  :min="FILLET_RADIUS_MIN"
                  :precision="3"
                  :step="1"
                  @update:value="(value) => handleVariableRadiusCommit(value, 'radius1')"
                />
                <span class="field-unit">mm</span>
              </div>
            </PuppyFormItem>

            <template v-else>
              <PuppyFormItem>
                <template #icon>
                  <span class="field-label-text">距离1</span>
                </template>
                <div class="field-with-unit">
                  <n-input-number
                    size="small"
                    show-button
                    :value="formatNullableLengthForPropertyManager(selectedVariablePoint?.radius ?? null)"
                    :disabled="!selectedVariablePoint || !context.setFilletVariablePointRadius"
                    :min="FILLET_RADIUS_MIN"
                    :precision="3"
                    :step="1"
                    @update:value="(value) => handleVariableRadiusCommit(value, 'radius1')"
                  />
                  <span class="field-unit">mm</span>
                </div>
              </PuppyFormItem>

              <PuppyFormItem>
                <template #icon>
                  <span class="field-label-text">距离2</span>
                </template>
                <div class="field-with-unit">
                  <n-input-number
                    size="small"
                    show-button
                    :value="formatNullableLengthForPropertyManager(selectedVariablePoint?.radius2 ?? null)"
                    :disabled="!selectedVariablePoint || !context.setFilletVariablePointRadius"
                    :min="FILLET_RADIUS_MIN"
                    :precision="3"
                    :step="1"
                    @update:value="(value) => handleVariableRadiusCommit(value, 'radius2')"
                  />
                  <span class="field-unit">mm</span>
                </div>
              </PuppyFormItem>
            </template>

            <PuppyFormItem v-if="variableMode === 'asymmetric' && isFieldVisible('asymmetricRadiiFlip')">
              <template #icon>
                <span class="field-label-text">翻转</span>
              </template>
              <n-checkbox
                :checked="asymmetricRadiiFlipped"
                :disabled="!isFieldEnabled('asymmetricRadiiFlip') || !context.setFilletAsymmetricRadiiFlipped"
                @update:checked="(value) => context.setFilletAsymmetricRadiiFlipped?.(value)"
              >
                翻转半径方向
              </n-checkbox>
            </PuppyFormItem>

            <div class="button-row">
              <n-button
                size="small"
                :disabled="!selectedVariablePoint || !context.assignFilletRadiusToAllPoints"
                @click="handleAssignVariableRadiusToAll"
              >
                设定所有
              </n-button>
            </div>
          </template>

          <FaceFilletParametersSection
            v-if="isFaceMode"
            :context="context"
          />
        </div>
      </PuppyCollapseItem>

      <PartialEdgeParametersSection
        v-if="showPartialEdgeParametersPanel"
        :enabled="partialEdgeParametersEnabled"
        :editable="isFieldEnabled('partialEdgeParameters')"
        :partial-edges="selectedFilletPartialEdges"
        :selected-items="selectedItems"
        :active-reference-target="partialReferenceTarget"
        @update:enabled="(value) => context.setFilletPartialEdgeParametersEnabled?.(value)"
        @condition-change="handlePartialConditionChange"
        @value-commit="handlePartialValueCommit"
        @reference-active-change="handlePartialReferenceActiveChange"
        @reference-delete="handlePartialReferenceDelete"
      />

    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';
import {
  NButton,
  NCheckbox,
  NCollapse,
  NInputNumber,
  NSelect,
} from 'naive-ui';
import type {
  BlendPartialCondition,
  EdgeFilletMode,
  FilletType,
  VariableFilletMode,
  VariableFilletTransition,
  VariableRadiusControlPoint,
} from '@puppycad/cad-core';
import {
  convertMToMm,
  convertMmToM,
} from '@puppycad/cad-core';
import type { PropertyManagerContext } from '@/core';
import type { FilletFaceSetId } from '@/core/features/blend/stage-two';
import type { BlendPartialReferenceTarget } from '@/core/features/blend/partial-edge';
import type {
  BlendFieldAvailability,
  FilletPropertyPanelSpec,
} from '@/core/features/blend/panel-spec';
import { resolveFilletPropertyPanelSpec } from '@/core/features/blend/panel-spec';
import { isConstantFilletEdgeModeLocked } from '@/core/features/blend/fillet-selection-policy';
import PuppyCollapseItem from '@/components/PuppyCollapseItem/PuppyCollapseItem.vue';
import PuppyFormItem from '@/components/PuppyFormItem/PuppyFormItem.vue';
import PuppyInputSelect from '@/components/PuppyInputSelect/PuppyInputSelect.vue';
import BlendTypeGlyph from '../common/BlendTypeGlyph.vue';
import PartialEdgeParametersSection from './PartialEdgeParametersSection.vue';
import FaceFilletParametersSection from './FaceFilletParametersSection.vue';
import {
  DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT,
  resolveSelectedVariableRadiusPointKey,
  resolveVariableRadiusDisplayItems,
  type VariableRadiusDisplayItem,
} from '@/core/features/blend/variable-radius';

interface FilletPropertySectionProps {
  context: PropertyManagerContext;
}
type FilletPanelField = keyof FilletPropertyPanelSpec;
type SelectablePersistentItem = {
  persistentRef: string;
  label: string;
  kind?: string;
};
type FaceSetGroupDefinition = {
  id: FilletFaceSetId;
  label: string;
};
const props = defineProps<FilletPropertySectionProps>();
const context = toRef(props, 'context');
const defaultExpanded = ['type', 'items', 'parameters', 'partial-edge'];
const FILLET_RADIUS_MIN = 0.001;
const FILLET_RADIUS_MIN_M = convertMmToM(FILLET_RADIUS_MIN);
const filletTypeOptions = [
  { value: 'constant' as const, iconKey: 'fillet-constant' as const, title: '固定大小圆角', testId: 'fillet-type-option-constant' },
  { value: 'variable' as const, iconKey: 'fillet-variable' as const, title: '变量大小圆角', testId: 'fillet-type-option-variable' },
  { value: 'face' as const, iconKey: 'fillet-face' as const, title: '面圆角', testId: 'fillet-type-option-face' },
] satisfies ReadonlyArray<{
  value: FilletType;
  iconKey: 'fillet-constant' | 'fillet-variable' | 'fillet-face';
  title: string;
  testId: string;
}>;
const variableModeOptions = [
  { label: '对称', value: 'symmetric' },
  { label: '非对称', value: 'asymmetric' },
];
const variableTransitionOptions = [
  { label: '平滑过渡', value: 'smooth' },
  { label: '直线过渡', value: 'straight' },
];
const filletType = computed<FilletType>(() => context.value.filletType ?? 'constant');
const radiusValue = computed(() => formatLengthForPropertyManager(context.value.filletRadius ?? convertMmToM(10)));
const radius2Value = computed(() => formatLengthForPropertyManager(context.value.filletDefinition?.radius2 ?? convertMmToM(radiusValue.value)));
const asymmetricRadiiFlipped = computed(() => context.value.filletDefinition?.asymmetricRadiiFlipped === true);
const variablePoints = computed(() => context.value.filletVariablePoints ?? []);
const variableMode = computed<VariableFilletMode>(() => context.value.filletDefinition?.variableMode ?? 'symmetric');
const variableInstanceCount = computed(() => context.value.filletVariableInstanceCount ?? DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT);
const faceSets = computed(() => context.value.filletDefinition?.faceSets ?? []);
const activeFaceSetId = computed(() => context.value.filletActiveFaceSetId ?? null);
const edgeFilletMode = computed<EdgeFilletMode>(() => context.value.filletDefinition?.edgeFilletMode ?? 'symmetric');
const selectedItems = computed(() => context.value.filletItems ?? []);
const selectedFilletItemRef = computed(() => context.value.selectedFilletItemRef ?? null);
const filletSelectionTarget = computed(() => context.value.filletSelectionTarget ?? 'faces');
const partialEdgeParametersEnabled = computed(() => context.value.filletDefinition?.partialEdgeParametersEnabled === true);
const partialReferenceTarget = computed<BlendPartialReferenceTarget | null>(() => context.value.filletPartialReferenceTarget ?? null);
const supportSpec = computed(() => resolveFilletPropertyPanelSpec(filletType.value));
const isConstantMode = computed(() => filletType.value === 'constant');
const isVariableMode = computed(() => filletType.value === 'variable');
const isFaceMode = computed(() => filletType.value === 'face');
const isFaceSetMode = computed(() => isFaceMode.value);
const hasFaceSelectionInConstantItems = computed(() => (
  isConstantMode.value
  && selectedItems.value.some((item) => item.kind === 'face')
));
const parametersPanelTitle = computed(() => (isVariableMode.value ? '变半径参数' : '圆角参数'));
const profileValue = computed(() => (variableMode.value === 'asymmetric' ? 'elliptic' : 'circular'));
const profileOptions = computed(() => (
  variableMode.value === 'asymmetric'
    ? [{ label: '椭圆', value: 'elliptic' }]
    : [{ label: '圆形', value: 'circular' }]
));
const variableTransitionMode = computed<VariableFilletTransition>(() => (
  context.value.filletDefinition?.variableTransition ?? 'smooth'
));
const constantEdgeFilletMode = computed<EdgeFilletMode>(() => (
  hasFaceSelectionInConstantItems.value ? 'symmetric' : edgeFilletMode.value
));
const constantEdgeModeLocked = computed(() => (
  isConstantFilletEdgeModeLocked(selectedItems.value) || !context.value.setFilletEdgeFilletMode
));
const filletPartialEdges = computed(() => context.value.filletPartialEdges ?? []);
const selectedFilletEdgeItem = computed(() => (
  selectedItems.value.find((item) => (
    item.persistentRef === selectedFilletItemRef.value
    && item.kind === 'edge'
  )) ?? null
));
const variablePointItems = computed(() => resolveVariableRadiusDisplayItems(
  variablePoints.value,
  variableMode.value,
));
const selectedVariablePointKey = computed(() => resolveSelectedVariableRadiusPointKey(
  variablePoints.value,
  context.value.selectedFilletVariablePointKey ?? null,
));
const selectedVariablePoint = computed<VariableRadiusControlPoint | null>(() => (
  variablePoints.value.find((point) => point.key === selectedVariablePointKey.value) ?? null
));
const selectedFilletPartialEdges = computed(() => (
  selectedFilletEdgeItem.value
    ? (
      filletPartialEdges.value.some((partialEdge) => partialEdge.itemRef === selectedFilletEdgeItem.value?.persistentRef)
        ? filletPartialEdges.value.filter((partialEdge) => partialEdge.itemRef === selectedFilletEdgeItem.value?.persistentRef)
        : [{
          itemRef: selectedFilletEdgeItem.value.persistentRef,
          startCondition: 'none' as const,
          endCondition: 'none' as const,
        }]
    )
    : []
));
const showPartialEdgeParametersPanel = computed(() => (
  isFieldVisible('partialEdgeParameters')
  && selectedFilletEdgeItem.value !== null
  && selectedFilletPartialEdges.value.length > 0
));
const edgeFilletModeOptions = computed(() => [
  { label: '对称', value: 'symmetric', disabled: !context.value.setFilletEdgeFilletMode },
  { label: '非对称', value: 'asymmetric', disabled: !context.value.setFilletEdgeFilletMode || hasFaceSelectionInConstantItems.value },
]);
const faceSetGroups = computed(() => {
  const groupDefinitions: FaceSetGroupDefinition[] = isFaceMode.value
    ? [
      { id: 'faceSet1', label: '面组1' },
      { id: 'faceSet2', label: '面组2' },
    ]
    : [];
  return groupDefinitions.map((group) => {
    const itemRefs = faceSets.value.find((faceSet) => faceSet.setId === group.id)?.items ?? [];
    return {
      ...group,
      items: itemRefs.map((itemRef, index) => {
        const displayItem = selectedItems.value.find((item) => item.persistentRef === itemRef.persistentRef);
        return displayItem ?? {
          persistentRef: itemRef.persistentRef,
          label: `面<${index + 1}>`,
        };
      }),
    };
  });
});
/**
 * 读取字段可用性。
 *
 * @param field 字段标识。
 * @returns 字段可用性。
 */
function resolveFieldAvailability(field: FilletPanelField): BlendFieldAvailability {
  return supportSpec.value[field];
}
/**
 * 判断字段是否可见。
 *
 * @param field 字段标识。
 * @returns 是否可见。
 */
function isFieldVisible(field: FilletPanelField): boolean {
  return resolveFieldAvailability(field) !== 'hidden';
}

/**
 * 判断字段是否可编辑。
 *
 * @param field 字段标识。
 * @returns 是否可编辑。
 */
function isFieldEnabled(field: FilletPanelField): boolean {
  return resolveFieldAvailability(field) === 'enabled';
}

/**
 * 按持久化引用解析当前激活索引。
 *
 * @param items 输入列表。
 * @param persistentRef 持久化引用。
 * @returns 激活索引；缺失时返回 -1。
 */
function resolveActiveIndex(
  items: SelectablePersistentItem[],
  persistentRef: string | null,
): number {
  if (!persistentRef) {
    return -1;
  }
  return items.findIndex((item) => item.persistentRef === persistentRef);
}

/**
 * 按持久化引用解析当前选中索引集合。
 *
 * @param items 输入列表。
 * @param persistentRef 持久化引用。
 * @returns 选中索引集合。
 */
function resolveSelectedIndices(
  items: SelectablePersistentItem[],
  persistentRef: string | null,
): number[] {
  const index = resolveActiveIndex(items, persistentRef);
  return index >= 0 ? [index] : [];
}

//------ 圆角属性长度单位转换 开始------
/**
 * 将底层米制长度转换为 PropertyManager 的毫米显示值。
 *
 * @param value 底层米制长度。
 * @returns 毫米显示值。
 */
function formatLengthForPropertyManager(value: number): number {
  // UI 中显示的 90 表示 90mm，底层存储的 0.09m 必须先转换后展示。
  return Number(convertMToMm(value).toFixed(3));
}

/**
 * 将可空底层米制长度转换为 PropertyManager 的毫米显示值。
 *
 * @param value 底层米制长度。
 * @returns 毫米显示值；无值时返回 null。
 */
function formatNullableLengthForPropertyManager(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value)
    ? formatLengthForPropertyManager(value)
    : null;
}

/**
 * 将 PropertyManager 的毫米输入转换为底层米制长度。
 *
 * @param value 用户输入的毫米值。
 * @returns 米制长度。
 */
function normalizeLengthFromPropertyManager(value: number | null): number {
  const normalizedMm = Math.max(FILLET_RADIUS_MIN, value ?? FILLET_RADIUS_MIN);
  return Math.max(FILLET_RADIUS_MIN_M, convertMmToM(normalizedMm));
}
//------ 圆角属性长度单位转换 结束------

/**
 * 切换圆角类型。
 *
 * @param type 目标圆角类型。
 */
function handleFilletTypeChange(type: FilletType): void {
  context.value.setFilletType?.(type);
}

/**
 * 激活主选择输入框。
 *
 * @param active 是否激活。
 */
function handlePrimaryInputActiveChange(active: boolean): void {
  if (active) {
    context.value.setFilletSelectionTarget?.('faces');
  }
}

/**
 * 激活面组输入框。
 *
 * @param setId 目标面组。
 * @param active 是否激活。
 */
function handleFaceSetInputActiveChange(setId: FilletFaceSetId, active: boolean): void {
  if (!active) {
    return;
  }
  context.value.setFilletFaceSetTarget?.(setId);
  context.value.setFilletSelectionTarget?.('faces');
}

/**
 * 处理主选择列表点击。
 *
 * @param item 被点击的列表项。
 */
function handlePrimaryItemClick(item: unknown): void {
  const selectionItem = item as SelectablePersistentItem;
  context.value.selectFilletItem?.(selectionItem.persistentRef);
}

/**
 * 删除主选择列表项。
 *
 * @param item 被删除的列表项。
 */
function handlePrimaryItemRemove(item: unknown): void {
  const selectionItem = item as SelectablePersistentItem;
  context.value.removeFilletItem?.(selectionItem.persistentRef);
}

/**
 * 清空主选择列表。
 */
function handlePrimaryDeleteAll(): void {
  selectedItems.value.forEach((item) => {
    context.value.removeFilletItem?.(item.persistentRef);
  });
}

/**
 * 处理面组项点击。
 *
 * @param setId 当前面组。
 * @param item 被点击的列表项。
 */
function handleFaceSetItemClick(setId: FilletFaceSetId, item: unknown): void {
  const selectionItem = item as SelectablePersistentItem;
  context.value.setFilletFaceSetTarget?.(setId);
  context.value.selectFilletItem?.(selectionItem.persistentRef);
}

/**
 * 处理面组项删除。
 *
 * @param setId 当前面组。
 * @param item 被删除的列表项。
 */
function handleFaceSetItemRemove(setId: FilletFaceSetId, item: unknown): void {
  const selectionItem = item as SelectablePersistentItem;
  context.value.setFilletFaceSetTarget?.(setId);
  context.value.removeFilletItem?.(selectionItem.persistentRef);
}

/**
 * 清空指定面组。
 *
 * @param setId 目标面组。
 */
function handleFaceSetDeleteAll(setId: FilletFaceSetId): void {
  const targetGroup = faceSetGroups.value.find((group) => group.id === setId);
  targetGroup?.items.forEach((item) => {
    context.value.setFilletFaceSetTarget?.(setId);
    context.value.removeFilletItem?.(item.persistentRef);
  });
}

/**
 * 提交主半径。
 *
 * @param payload 输入载荷。
 */
function handleRadiusCommit(value: number | null): void {
  context.value.setFilletRadius?.(normalizeLengthFromPropertyManager(value), null);
}

/**
 * 提交次半径。
 *
 * @param payload 输入载荷。
 */
function handleRadius2Commit(value: number | null): void {
  context.value.setFilletRadius2?.(normalizeLengthFromPropertyManager(value), null);
}

/**
 * 切换常量圆角模式。
 *
 * @param value 目标模式。
 */
function handleEdgeFilletModeChange(value: string | null): void {
  if (value !== 'symmetric' && value !== 'asymmetric') {
    return;
  }
  if (hasFaceSelectionInConstantItems.value) {
    context.value.setFilletEdgeFilletMode?.('symmetric');
    return;
  }
  context.value.setFilletEdgeFilletMode?.(value);
}

/**
 * 切换变量圆角模式。
 *
 * @param value 目标模式。
 */
function handleVariableModeChange(value: string | null): void {
  if (value !== 'symmetric' && value !== 'asymmetric') {
    return;
  }
  context.value.setFilletVariableMode?.(value);
}

/**
 * 切换变量圆角过渡方式。
 *
 * @param value 目标过渡方式。
 */
function handleVariableTransitionChange(value: string | null): void {
  if (value !== 'smooth' && value !== 'straight') {
    return;
  }
  context.value.setFilletVariableTransition?.(value);
}

/**
 * 更新变量圆角实例数。
 *
 * @param value 实例数。
 */
function handleVariableInstanceCountChange(value: number | null): void {
  context.value.setFilletVariableInstanceCount?.(typeof value === 'number' ? value : DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT);
}

/**
 * 选择变量圆角控制点。
 *
 * @param item 被点击的列表项。
 */
function handleVariablePointItemClick(item: unknown): void {
  const selectionItem = item as SelectablePersistentItem;
  context.value.selectFilletVariablePoint?.(selectionItem.persistentRef);
}

/**
 * 判断变量半径点是否允许删除。
 *
 * @param item 半径点列表项。
 * @returns 是否允许删除。
 */
function isVariablePointDeleteEnabled(item: unknown): boolean {
  return (item as VariableRadiusDisplayItem | null)?.role === 'instance';
}

/**
 * 删除变量圆角实例点。
 *
 * @param item 被删除的列表项。
 */
function handleVariablePointItemRemove(item: unknown): void {
  if (!isVariablePointDeleteEnabled(item)) {
    return;
  }
  const selectionItem = item as SelectablePersistentItem;
  context.value.removeFilletVariablePoint?.(selectionItem.persistentRef);
}

/**
 * 删除所有变量圆角实例点。
 */
function handleVariablePointDeleteAll(): void {
  context.value.removeAllFilletVariableInstancePoints?.();
}

/**
 * 提交当前变量圆角控制点。
 *
 * @param payload 输入载荷。
 */
function handleVariableRadiusCommit(
  value: number | null,
  slot: 'radius1' | 'radius2' = 'radius1',
): void {
  if (!selectedVariablePointKey.value) {
    return;
  }
  context.value.setFilletVariablePointRadius?.(
    selectedVariablePointKey.value,
    normalizeLengthFromPropertyManager(value),
    null,
    slot,
  );
}

/**
 * 把当前控制点半径写入所有变量圆角控制点。
 */
function handleAssignVariableRadiusToAll(): void {
  if (!selectedVariablePoint.value || typeof selectedVariablePoint.value.radius !== 'number') {
    return;
  }
  if (variableMode.value === 'asymmetric' && typeof selectedVariablePoint.value.radius2 !== 'number') {
    return;
  }
  context.value.assignFilletRadiusToAllPoints?.(
    selectedVariablePoint.value.radius,
    null,
    variableMode.value === 'asymmetric' ? selectedVariablePoint.value.radius2 ?? undefined : undefined,
    null,
  );
}

/**
 * 切换局部边条件。
 *
 * @param itemRef 对应边引用。
 * @param side 起止侧。
 * @param value 目标条件。
 */
function handlePartialConditionChange(
  itemRef: string,
  side: 'start' | 'end',
  value: BlendPartialCondition,
): void {
  context.value.setFilletPartialCondition?.(itemRef, side, value);
}

/**
 * 提交局部边数值。
 *
 * @param itemRef 对应边引用。
 * @param side 起止侧。
 * @param payload 输入载荷。
 */
function handlePartialValueCommit(
  itemRef: string,
  side: 'start' | 'end',
  value: number,
): void {
  context.value.setFilletPartialValue?.(itemRef, side, Math.max(0, value), null);
}

/**
 * 切换部分边线参考选择输入框。
 *
 * @param itemRef 对应边引用。
 * @param side 起止侧。
 * @param active 是否激活。
 */
function handlePartialReferenceActiveChange(
  itemRef: string,
  side: 'start' | 'end',
  active: boolean,
): void {
  context.value.setFilletPartialReferenceTarget?.(itemRef, side, active);
}

/**
 * 清空部分边线参考。
 *
 * @param itemRef 对应边引用。
 * @param side 起止侧。
 */
function handlePartialReferenceDelete(
  itemRef: string,
  side: 'start' | 'end',
): void {
  context.value.clearFilletPartialReference?.(itemRef, side);
}

</script>

<style scoped src="./BlendPropertySection.css"></style>
